import { InspectionValues, StepTypes } from '@parallel-fluidics/shared/constants';
import { travelerFromFirestore } from '@parallel-fluidics/shared/models/travelers';
import PPartDisplay from 'components/Projects/pPartDisplay';
import {
  collection,
  getFirestore,
  query,
  where,
} from 'firebase/firestore';
import useRealtimeDocs from 'hooks/use-realtimeDocs';
import { useTrackersData } from 'pages/Trackers/trackersProvider';
import PropTypes from 'prop-types';
import React, {
  forwardRef,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { stepType } from 'utils/reactFlowUtils';

import Chips, {
  chipHeight,
  chipWidth,
  projectStatusType,
  queryStatusType,
} from './chips';
import ProgressCircle from './progressCircle';
import { useSelection } from './selectionProvider';
import ShippingDate from './shippingDate';

const statusType = PropTypes.oneOf(['completed', 'inProgress', 'stalled']);

export const useStepStatus = (stepData) => {
  // fetch travelers for the current step based on lineItemIds
  const travelersQuery = useMemo(() => query(
    collection(getFirestore(), 'travelers'),
    where('lineItem', 'in', stepData.lineItemIds),
  ), [stepData.lineItemIds]);

  const { data: travelers, status: queryStatus } = useRealtimeDocs({
    query: travelersQuery,
    transformData: travelerFromFirestore,
  });

  const projectStatus = useMemo(() => {
    if (queryStatus === 'success') {
      const initializePart = (step) => ({
        passed: 0,
        toBeInspected: 0,
        onDeck: 0,
        rejectCosmetic: 0,
        rejectMajor: 0,
        tuning: 0,
        unknown: 0,
        stepYield: 0,
        chipData: [],
        type: step.type,
      });
      // use index instead of name, because name is not unique
      const retParts = initializePart(stepData) || {};
      if (!travelers.length) return retParts;

      travelers.forEach((traveler) => {
        const currentStep = traveler.steps[stepData.stepIdx];
        const prevStep = traveler.steps[stepData.stepIdx - 1];

        // if previous step is not inspected, skip current step calculation
        if (prevStep && prevStep.inspected === InspectionValues.NOT_INSPECTED) return;
        // if previous step is not passed and current step is not inspected,
        // skip current step calculation
        if (prevStep
          && prevStep.inspected !== InspectionValues.PASSED
          && currentStep.inspected === InspectionValues.NOT_INSPECTED
        ) {
          return;
        }

        const isPrevOnDeck = prevStep
        && prevStep.inspected === InspectionValues.NOT_INSPECTED
        && [StepTypes.POSTMACHINING, StepTypes.CONNECTORIZING].includes(prevStep.type)
        && !prevStep.runId?.length;

        const lastRun = currentStep.runId?.[currentStep.runId.length - 1];

        const addChipData = (curInspection) => {
          retParts.chipData.push({
            travelerId: traveler.id,
            prevInspection: isPrevOnDeck ? 'On deck' : (prevStep?.inspected || InspectionValues.PASSED),
            curInspection,
            runStartDate: lastRun?.start?.toDate(),
            runEndDate: lastRun?.end?.toDate(),
            press: lastRun?.press,
            processId: lastRun?.id,
            cavity: lastRun?.cavity,
            // eslint-disable-next-line no-underscore-dangle
            sortDate: lastRun?.start || traveler._createdBy.timestamp,
            runID: lastRun?.runID,
            inspectedNote: currentStep.inspectedNote,
            failureTags: currentStep.failureTags,
            shipped: traveler.shipped,
          });
        };

        const addToDeck = () => {
          retParts.onDeck += 1;
          addChipData('On deck');
        };

        // check if it's on deck for MOLDING and BONDING
        if (
          !currentStep.runId?.length
          && [StepTypes.MOLDING, StepTypes.BONDING].includes(currentStep.type)
        ) {
          addToDeck();
          return;
        }

        if (currentStep.inspected === InspectionValues.NOT_INSPECTED) {
          // for POSTMACHINING and CONNECTORIZING, we want show on deck instead of toBeInspected
          if ([StepTypes.POSTMACHINING, StepTypes.CONNECTORIZING].includes(currentStep.type)) {
            addToDeck();
            return;
          }
          retParts.toBeInspected += 1;
        } else if (currentStep.inspected === InspectionValues.PASSED) {
          retParts.passed += 1;
        } else if (currentStep.inspected === InspectionValues.REJECT_MAJOR) {
          retParts.rejectMajor += 1;
        } else if (currentStep.inspected === InspectionValues.REJECT_COSMETIC) {
          retParts.rejectCosmetic += 1;
        } else if (currentStep.inspected === InspectionValues.TUNING) {
          // Calculate the turning count independently of the previous stage
          retParts.tuning += 1;
        } else {
          retParts.unknown += 1;
        }
        addChipData(currentStep.inspected);
      });

      // calculate step yield rate
      const part = retParts;
      part.stepYield = Math.round(
        (part.passed / ((part.passed + part.rejectCosmetic + part.rejectMajor) || 1)) * 100,
      );
      // sort chipData by sortDate
      part.chipData.sort((a, b) => a.sortDate - b.sortDate);

      return retParts;
    }
    return undefined;
  }, [queryStatus, stepData, travelers]);
  return { projectStatus, queryStatus };
};

const Node = forwardRef(({
  type,
  node,
  levelIdx = undefined,
  expandTo = undefined,
  updatePaths,
  updateNodeStatus,
}, ref) => {
  const { projectStatus, queryStatus } = useStepStatus(node);
  const [status, setStatus] = useState();

  useEffect(() => {
    updatePaths();
  }, [projectStatus?.passed, queryStatus, updatePaths]);

  useEffect(() => {
    if (!projectStatus) return;
    switch (true) {
      case projectStatus.passed >= node.goalSum:
        setStatus('completed');
        updateNodeStatus('completed');
        break;
      case projectStatus.onDeck > 0
        || projectStatus.toBeInspected > 0
        || levelIdx === 0:
        setStatus('inProgress');
        updateNodeStatus('inProgress');
        break;
      case projectStatus.onDeck === 0:
        setStatus('stalled');
        updateNodeStatus('stalled');
        break;
      default:
    }
  }, [projectStatus, queryStatus, levelIdx, node.goalSum, updateNodeStatus]);

  return (
    <li ref={ref}>
      {type === 'collapsed' ? (
        <CollapsedNode
          status={status}
          node={node}
          projectStatus={projectStatus}
          queryStatus={queryStatus}
          expandTo={expandTo}
        />
      ) : (
        <ExpandedNode
          status={status}
          node={node}
          projectStatus={projectStatus}
          queryStatus={queryStatus}
        />
      )}
    </li>
  );
});

Node.propTypes = {
  type: PropTypes.oneOf(['collapsed', 'expanded']).isRequired,
  node: stepType.isRequired,
  levelIdx: PropTypes.number,
  expandTo: PropTypes.func,
  updatePaths: PropTypes.func.isRequired,
  updateNodeStatus: PropTypes.func.isRequired,
};

Node.defaultProps = {
  levelIdx: undefined,
  expandTo: undefined,
};

export default Node;

function CollapsedNode({
  status = undefined,
  node,
  projectStatus = undefined,
  queryStatus,
  expandTo = undefined,
}) {
  const { groupedOrdersByProject } = useTrackersData();
  const { lineItemsMap } = groupedOrdersByProject[node.projectId];
  const lineItem = lineItemsMap[node.lineItemId] || {};

  return (
    <button
      className={`
        node
        collapsed
        ${status ?? ''}
      `}
      type="button"
      onClick={() => expandTo?.(node.uniqueId)}
    >
      {node.pPartId && (
        <span className={`badge${node.isShippingPoint ? ' finalLineItem' : ''}`}>
          {node.pPartId}
        </span>
      )}
      <span className="name">
        <PPartDisplay
          fieldName="name"
          projectId={node.projectId}
          projectDocId={node.projectDocId}
          pPartId={node.pPartId}
          pPartDocId={node.pPartDocId}
          fallbackMessage={node.name || node.type}
        />
      </span>
      <ProgressCircle
        passed={queryStatus === 'success' && projectStatus ? projectStatus.passed : 0}
        goal={node.goalSum}
      />
      <span className="progress">
        <span className="passed">
          {queryStatus === 'success' && projectStatus ? projectStatus.passed : '-'}
        </span>
        /
        {node.goalSum}
      </span>
      {node.isShippingPoint && (
        <ShippingDate
          shipDateTarget={lineItem.shipDateTarget}
          shipDateActual={lineItem.shipDateActual}
          shipDateBusinessDays={lineItem.shipDateBusinessDays}
          isInternal={lineItem.projectId.startsWith('P')}
        />
      )}
    </button>
  );
}

function NotesWrapper({ children }) {
  return (
    <div className="notes">{children}</div>
  );
}

NotesWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

CollapsedNode.propTypes = {
  status: statusType,
  node: stepType.isRequired,
  projectStatus: projectStatusType,
  queryStatus: queryStatusType.isRequired,
  expandTo: PropTypes.func,
};

function ExpandedNode({
  status = undefined,
  node,
  projectStatus = undefined,
  queryStatus,
}) {
  const { activeNode, setActiveNode } = useSelection();
  const isActive = useMemo(
    () => activeNode === node.uniqueId,
    [activeNode, node.uniqueId],
  );

  return (
    <div
      id={node.uniqueId}
      className={`
        node
        expanded
        ${status ?? ''}
        ${isActive ? 'active' : ''}
      `}
      tabIndex="-1"
      style={{
        '--_chipWidth': `${chipWidth}px`,
        '--_chipHeight': `${chipHeight}px`,
      }}
      onFocus={() => setActiveNode(node.uniqueId)}
    >
      <div className="header">
        {node.pPartId && (
          <p className={`badge${node.isShippingPoint ? ' finalLineItem' : ''}`}>
            {node.pPartId}
          </p>
        )}
        <h4 className="name">
          <PPartDisplay
            fieldName="name"
            projectId={node.projectId}
            projectDocId={node.projectDocId}
            pPartId={node.pPartId}
            pPartDocId={node.pPartDocId}
            fallbackMessage={node.name || node.type}
          />
        </h4>
        <ProgressCircle
          passed={queryStatus === 'success' ? projectStatus.passed : 0}
          goal={node.goalSum}
        />
        <p className="yield">
          {queryStatus === 'success' ? projectStatus.stepYield : '- '}
          % yield
        </p>
      </div>

      <PPartDisplay
        fieldName="notes"
        projectId={node.projectId}
        projectDocId={node.projectDocId}
        pPartId={node.pPartId}
        pPartDocId={node.pPartDocId}
        fallbackMessage={node.note}
        CustomWrapper={NotesWrapper}
      />

      <Chips
        node={node}
        queryStatus={queryStatus}
        projectStatus={projectStatus}
      />
    </div>
  );
}

ExpandedNode.propTypes = {
  status: statusType,
  node: stepType.isRequired,
  projectStatus: projectStatusType,
  queryStatus: queryStatusType.isRequired,
};
